/**
 * Cache management utilities
 * Handles cache eviction, cleanup, and lifecycle management
 */

import { systemLogger } from '@/lib/logging';
import type { CacheEntry, CacheConfig } from '../types/cache-types';
import { CacheIntegrity } from './cache-integrity';

export class CacheManagement {
  private cleanupTimer: number | null = null;
  private cacheIntegrity = new CacheIntegrity();

  constructor(private _config: CacheConfig) {}

  /**
   * Get cache configuration
   */
  get config(): CacheConfig {
    return this._config;
  }

  /**
   * Start automatic cleanup timer
   */
  startCleanupTimer(cache: Map<string, CacheEntry>): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = window.setInterval(() => {
      this.cleanup(cache);
    }, this.config.cleanupInterval);
  }

  /**
   * Stop cleanup timer
   */
  stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Perform cache cleanup - remove expired entries
   */
  cleanup(cache: Map<string, CacheEntry>): void {
    const beforeSize = cache.size;
    let removedCount = 0;

    for (const [key, entry] of cache.entries()) {
      if (this.cacheIntegrity.isExpired(entry)) {
        cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      systemLogger.debug(`Cache cleanup: removed ${removedCount} expired entries`, {
        beforeSize,
        afterSize: cache.size,
        removedCount,
      });
    }
  }

  /**
   * Evict entries if cache is at capacity
   */
  evictIfNecessary(cache: Map<string, CacheEntry>): number {
    let evictedCount = 0;

    if (cache.size >= this.config.maxEntries) {
      // LRU eviction - remove oldest entries first
      const entries = Array.from(cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toEvict = Math.max(1, Math.floor(this.config.maxEntries * 0.1)); // Evict 10%

      for (let i = 0; i < toEvict && cache.size > 0; i++) {
        const entry = entries[i];
        if (entry) {
          const [key] = entry;
          if (cache.has(key)) {
            cache.delete(key);
            evictedCount++;
          }
        }
      }

      if (evictedCount > 0) {
        systemLogger.debug(`Cache eviction: removed ${evictedCount} entries`, {
          totalEntries: cache.size,
          evictedCount,
        });
      }
    }

    return evictedCount;
  }

  /**
   * Clear all cache entries
   */
  clearAll(cache: Map<string, CacheEntry>): void {
    const entryCount = cache.size;
    cache.clear();

    systemLogger.info(`Cache cleared: removed ${entryCount} entries`);
  }

  /**
   * Remove entries for specific user
   */
  clearUserEntries(cache: Map<string, CacheEntry>, userId: string): void {
    let removedCount = 0;

    for (const [key, entry] of cache.entries()) {
      if (entry.userId === userId) {
        cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      systemLogger.debug(`Cache cleared for user: ${userId}`, {
        removedCount,
        remainingEntries: cache.size,
      });
    }
  }

  /**
   * Estimate memory usage of cache
   */
  estimateMemoryUsage(cache: Map<string, CacheEntry>): number {
    let totalSize = 0;

    for (const [key, entry] of cache.entries()) {
      // Estimate size of key and entry
      totalSize += key.length * 2; // UTF-16 characters
      totalSize += JSON.stringify(entry).length * 2;
    }

    return totalSize; // bytes
  }

  /**
   * Get cache entry statistics
   */
  getEntryStatistics(cache: Map<string, CacheEntry>): {
    totalEntries: number;
    expiredEntries: number;
    averageAge: number;
    oldestEntry: number;
    newestEntry: number;
  } {
    const now = Date.now();
    let expiredCount = 0;
    let totalAge = 0;
    let oldestTime = now;
    let newestTime = 0;

    for (const entry of cache.values()) {
      const age = now - entry.timestamp;
      totalAge += age;

      if (this.cacheIntegrity.isExpired(entry)) {
        expiredCount++;
      }

      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
      }

      if (entry.timestamp > newestTime) {
        newestTime = entry.timestamp;
      }
    }

    return {
      totalEntries: cache.size,
      expiredEntries: expiredCount,
      averageAge: cache.size > 0 ? totalAge / cache.size : 0,
      oldestEntry: oldestTime,
      newestEntry: newestTime,
    };
  }
}